import { LayerData, selectedLandXMLFeatureAtom, tLayerSettings } from '@/atoms';
import {
  LandXMLCategory,
  LandXMLFeature,
  parseAlignments,
  parseBreaklines,
  parseParcels,
  parsePipeNetworks,
  parseSurfaces,
  projectPointOntoAlignment,
} from '@/core/landxml';
import { useLayerSettingsMany } from '@/hooks/useLayerSettings';
import { sampleColormap } from '@/lib/colormaps';
import { useThree } from '@react-three/fiber';
import { useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import PickableGroup from './3D/PickableGroup';

/**
 * Per-layer entry: the parsed features (with their world-space origin offset)
 * plus a fast lookup of feature → metadata for the click handler.
 */
interface LayerEntry {
  layerId: string;
  origin: THREE.Vector3;
  features: LandXMLFeature[];
}

const FEATURE_ID_KEY = 'landxmlFeatureId';
const FEATURE_LAYER_KEY = 'landxmlLayerId';

/** Build a colored material from a single hex string. */
function makeSolidMaterial(hex: string, opacity: number, isWireframe: boolean, double = true) {
  return new THREE.MeshLambertMaterial({
    color: hex,
    side: double ? THREE.DoubleSide : THREE.FrontSide,
    wireframe: isWireframe,
    transparent: opacity < 1,
    opacity,
  });
}

/**
 * Vertex-color a TIN by elevation or slope, using the chosen colormap.
 * Mutates the geometry by attaching a 'color' attribute.
 *
 * For ELEVATION we recompute min/max from the geometry's current position
 * buffer rather than trusting any pre-shift metadata: the load effect
 * translates each geometry by `-finalOrigin`, so absolute Z values would
 * map to t≈0 and everything would render blue.
 */
function applyVertexColors(
  geometry: THREE.BufferGeometry,
  mode: 'ELEVATION' | 'SLOPE',
  colormap: string,
) {
  const pos = geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
  if (!pos) return;
  const count = pos.count;
  const colors = new Float32Array(count * 3);
  const c = new THREE.Color();
  if (mode === 'ELEVATION') {
    let zMin = Infinity;
    let zMax = -Infinity;
    for (let i = 0; i < count; i++) {
      const z = pos.getZ(i);
      if (z < zMin) zMin = z;
      if (z > zMax) zMax = z;
    }
    const range = Math.max(1e-9, zMax - zMin);
    for (let i = 0; i < count; i++) {
      const t = (pos.getZ(i) - zMin) / range;
      sampleColormap(colormap, t, c);
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
  } else {
    // SLOPE: angle between face-normal and Z axis
    let normals = geometry.getAttribute('normal') as THREE.BufferAttribute | undefined;
    if (!normals) {
      geometry.computeVertexNormals();
      normals = geometry.getAttribute('normal') as THREE.BufferAttribute | undefined;
    }
    if (!normals) return;
    for (let i = 0; i < count; i++) {
      const nz = Math.min(1, Math.max(-1, normals.getZ(i)));
      const angle = Math.acos(Math.abs(nz)); // 0 = flat, π/2 = vertical
      const t = angle / (Math.PI / 2);
      sampleColormap(colormap, t, c);
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
}

/**
 * Pick the right hex color for a feature given the current layer settings.
 * Returns null when surfaces should be vertex-colored instead.
 */
function colorForCategory(category: LandXMLCategory, settings: tLayerSettings): string {
  const colors = settings.landxmlCategoryColors;
  switch (category) {
    case 'SURFACE':
      return colors?.surface ?? settings.modelDefaultColor;
    case 'ALIGNMENT':
      return colors?.alignment ?? '#e53935';
    case 'PARCEL':
      return colors?.parcel ?? '#43a047';
    case 'BREAKLINE':
      return colors?.breakline ?? '#787878';
    case 'PIPE':
      return colors?.pipe ?? '#8b6914';
    case 'STRUCTURE':
      return colors?.structure ?? '#607d8b';
  }
}

export const LandXML = ({ layers }: { layers: LayerData[] }) => {
  const [entries, setEntries] = useState<LayerEntry[]>([]);
  const layerSettings = useLayerSettingsMany(layers.map((l) => l.id));
  const setSelected = useSetAtom(selectedLandXMLFeatureAtom);
  const raycaster = useThree((s) => s.raycaster);

  // Three.js' default Line raycast threshold is 1 world-unit — far too tight
  // for civil-scale alignments, where a typical zoom level makes that a
  // single-pixel hitbox. Bump it while LandXML is mounted so double-clicking
  // a thin alignment/parcel/breakline actually registers.
  useEffect(() => {
    if (!raycaster.params.Line) return;
    const prev = raycaster.params.Line.threshold;
    raycaster.params.Line.threshold = Math.max(prev, 3);
    return () => {
      raycaster.params.Line!.threshold = prev;
    };
  }, [raycaster]);

  // Feature registry indexed by `${layerId}:${featureId}` so the click
  // handler can recover full metadata from a raycast hit's userData.
  const registryRef = useRef<Map<string, LandXMLFeature & { layerId: string }>>(new Map());

  // Stable load key — re-parse only when the actual file or layer set changes.
  const layerLoadKey = useMemo(
    () => layers.map((l) => `${l.id}:${l.root}`).join(','),
    [layers],
  );

  // --- Parse + load LandXML files ----------------------------------------
  useEffect(() => {
    const abort = new AbortController();
    setEntries([]);
    registryRef.current.clear();

    const loadOne = async (file: string, layerId: string) => {
      try {
        const response = await fetch(`${import.meta.env.VITE_RESOURCES_ENDPOINT}/${file}`, {
          signal: abort.signal,
          credentials: 'include',
        });
        const text = await response.text();
        if (abort.signal.aborted) return;
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'application/xml');
        // console.log(doc); // kept around for ad-hoc inspection — re-enable when investigating parser issues.
        const parserError = doc.querySelector('parsererror');
        if (parserError) {
          console.error('LandXML parse error:', parserError.textContent);
          return;
        }

        const surfaces = parseSurfaces(doc);
        const breaklines = parseBreaklines(doc);
        const alignments = parseAlignments(doc);
        const parcels = parseParcels(doc);
        const pipeNetworks = parsePipeNetworks(doc);

        // Compute origin from first available vertex (any feature).
        let origin: THREE.Vector3 | null = null;
        const all: LandXMLFeature[] = [
          ...surfaces,
          ...alignments,
          ...parcels,
          ...pipeNetworks.lines,
          ...pipeNetworks.meshes,
          ...breaklines,
        ];
        for (const f of all) {
          const pos = f.geometry.getAttribute('position');
          if (pos && pos.count > 0) {
            origin = new THREE.Vector3(pos.getX(0), pos.getY(0), pos.getZ(0));
            break;
          }
        }
        const finalOrigin = origin ?? new THREE.Vector3();

        // Offset every geometry to world-relative space so the parent group
        // can position the whole model at `origin`.
        for (const f of all) {
          f.geometry.translate(-finalOrigin.x, -finalOrigin.y, -finalOrigin.z);
        }

        // Adjust alignment metadata polylines too (they're absolute, used for
        // stationing — drawer translates clickPoint back into the same frame).
        for (const f of alignments) {
          if (f.metadata.polyline) {
            for (const p of f.metadata.polyline) p.sub(finalOrigin);
          }
        }

        if (abort.signal.aborted) return;

        // Tag each feature with a deterministic id and register it.
        const features: LandXMLFeature[] = [];
        all.forEach((f, idx) => {
          const featureId = String(idx);
          f.geometry.userData = {
            ...(f.geometry.userData ?? {}),
            [FEATURE_ID_KEY]: featureId,
            [FEATURE_LAYER_KEY]: layerId,
          };
          features.push(f);
          registryRef.current.set(`${layerId}:${featureId}`, { ...f, layerId });
        });

        setEntries((prev) => [...prev, { layerId, origin: finalOrigin, features }]);
      } catch (err) {
        if (!abort.signal.aborted) console.error('Error loading LandXML:', err);
      }
    };

    layers.forEach((layer) => layer.root && loadOne(layer.root, layer.id));
    return () => {
      abort.abort();
      // Dispose geometries from previous load.
      for (const e of entries) for (const f of e.features) f.geometry.dispose();
    };
    // We intentionally key off layerLoadKey, not the in-flight `entries` array.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layerLoadKey]);

  // --- Build materials per (layer, category) -----------------------------
  // Re-creates whenever color settings change. Surface materials are special
  // when the user picks ELEVATION / SLOPE — they use vertexColors.
  const materialsByLayer = useMemo(() => {
    const map = new Map<string, Record<LandXMLCategory, THREE.Material> & { surfaceVertexColored?: THREE.Material }>();
    for (const e of entries) {
      const s = layerSettings[e.layerId];
      if (!s) continue;
      const opacity = s.modelOpacity ?? 1.0;
      const isWireframe = s.modelIsWireframe;
      const surfaceMode = s.landxmlSurfaceColorMode ?? 'SOLID';

      const cats: Record<LandXMLCategory, THREE.Material> = {
        SURFACE: makeSolidMaterial(colorForCategory('SURFACE', s), opacity, isWireframe),
        ALIGNMENT: new THREE.LineBasicMaterial({ color: colorForCategory('ALIGNMENT', s) }),
        PARCEL: new THREE.LineBasicMaterial({ color: colorForCategory('PARCEL', s) }),
        BREAKLINE: new THREE.LineBasicMaterial({ color: colorForCategory('BREAKLINE', s) }),
        PIPE: makeSolidMaterial(colorForCategory('PIPE', s), opacity, isWireframe),
        STRUCTURE: makeSolidMaterial(colorForCategory('STRUCTURE', s), opacity, isWireframe),
      };
      const surfaceVertexColored = surfaceMode !== 'SOLID'
        ? new THREE.MeshLambertMaterial({
            vertexColors: true,
            side: THREE.DoubleSide,
            wireframe: isWireframe,
            transparent: opacity < 1,
            opacity,
          })
        : undefined;
      map.set(e.layerId, { ...cats, surfaceVertexColored });
    }
    return map;
  }, [entries, layerSettings]);

  // Apply or strip vertex colors on surface geometries when mode/colormap changes.
  useEffect(() => {
    for (const e of entries) {
      const s = layerSettings[e.layerId];
      if (!s) continue;
      const mode = s.landxmlSurfaceColorMode ?? 'SOLID';
      const colormap = s.landxmlSurfaceColormap ?? 'SPECTRAL';
      for (const f of e.features) {
        if (f.category !== 'SURFACE') continue;
        if (mode === 'SOLID') {
          f.geometry.deleteAttribute('color');
        } else {
          applyVertexColors(f.geometry, mode as 'ELEVATION' | 'SLOPE', colormap);
        }
      }
    }
  }, [entries, layerSettings]);

  // Cleanup geometries on unmount.
  useEffect(() => {
    const captured = entries;
    return () => {
      for (const e of captured) for (const f of e.features) f.geometry.dispose();
    };
  }, [entries]);

  // r3f click handler at the group level — recovers the picked feature from
  // the intersected object's geometry.userData. Click point is converted
  // to the object's local space so it lives in the same frame as the
  // polyline metadata (which was translated by -origin at load time).
  const _handleClick = (e: { stopPropagation: () => void; object: THREE.Object3D; point: THREE.Vector3 }) => {
    const obj = e.object as THREE.Mesh | THREE.Line;
    const ud = (obj.geometry as THREE.BufferGeometry | undefined)?.userData ?? {};
    const featureId = ud[FEATURE_ID_KEY];
    const layerId = ud[FEATURE_LAYER_KEY];
    if (!featureId || !layerId) return;
    const feat = registryRef.current.get(`${layerId}:${featureId}`);
    if (!feat) return;
    e.stopPropagation();
    const localPoint = obj.worldToLocal(e.point.clone());

    // For non-alignment picks, find the nearest alignment in the same layer
    // and report station + perpendicular offset there too — answers
    // "where on the road / centerline is this surface point?".
    let nearestAlignment: { name: string; featureId: string; station: number; offset: number } | undefined;
    if (feat.category !== 'ALIGNMENT') {
      for (const [key, candidate] of registryRef.current) {
        if (candidate.layerId !== layerId) continue;
        if (candidate.category !== 'ALIGNMENT') continue;
        const polyline = candidate.metadata.polyline;
        const cumLengths = candidate.metadata.cumLengths;
        if (!polyline || !cumLengths) continue;
        const projection = projectPointOntoAlignment(localPoint, polyline, cumLengths);
        if (!projection) continue;
        if (!nearestAlignment || projection.offset < nearestAlignment.offset) {
          nearestAlignment = {
            name: candidate.name,
            featureId: key.split(':')[1] ?? '',
            station: projection.station,
            offset: projection.offset,
          };
        }
      }
    }

    setSelected({
      layerId,
      featureId,
      name: feat.name,
      category: feat.category,
      metadata: feat.metadata,
      clickPoint: { x: localPoint.x, y: localPoint.y, z: localPoint.z },
      nearestAlignment,
    });
  };

  return (
    <>
      {entries
        .filter((e) => !!layerSettings[e.layerId])
        .map((e) => {
          const mats = materialsByLayer.get(e.layerId);
          if (!mats) return null;
          const surfaceMode = layerSettings[e.layerId]?.landxmlSurfaceColorMode ?? 'SOLID';
          return (
            <PickableGroup key={e.layerId} position={e.origin}>
              {e.features.map((f) => {
                const isLine =
                  f.category === 'ALIGNMENT' || f.category === 'PARCEL' || f.category === 'BREAKLINE';
                if (isLine) {
                  // r3f maps lowercase `line` to SVG in TS. Use a typed THREE.Line via primitive.
                  const lineObj = new THREE.Line(f.geometry, mats[f.category]);
                  lineObj.userData = { [FEATURE_ID_KEY]: (f.geometry.userData as any)?.[FEATURE_ID_KEY], [FEATURE_LAYER_KEY]: e.layerId };
                  return (
                    <primitive
                      key={`${e.layerId}-${(f.geometry.userData as any)?.[FEATURE_ID_KEY]}`}
                      object={lineObj}
                      onDoubleClick={_handleClick}
                    />
                  );
                }
                const useVertexColored = f.category === 'SURFACE' && surfaceMode !== 'SOLID' && mats.surfaceVertexColored;
                const mat = useVertexColored ? mats.surfaceVertexColored! : mats[f.category];
                return (
                  <mesh
                    key={`${e.layerId}-${(f.geometry.userData as any)?.[FEATURE_ID_KEY]}`}
                    geometry={f.geometry}
                    material={mat}
                    onDoubleClick={_handleClick}
                  />
                );
              })}
            </PickableGroup>
          );
        })}
    </>
  );
};
